import { ErrorHandler, type ApplicationConfig } from '@angular/core';
import { isDevMode } from '@angular/core';
import { provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { provideServiceWorker } from '@angular/service-worker';
import {
  provideRouter,
  withViewTransitions,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { cacheInterceptor } from './core/interceptors/cache.interceptor';
import { languageInterceptor } from './core/interceptors/language.interceptor';
import { correlationInterceptor } from './core/interceptors/correlation.interceptor';
import { provideClientHydration, withHttpTransferCacheOptions } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { routes } from './app.routes';
import {
  PORTFOLIO_REPOSITORY,
  ADMIN_REPOSITORY,
  BLOG_REPOSITORY,
  GUESTBOOK_REPOSITORY,
} from './core/domain/repositories';
import {
  HttpPortfolioRepository,
  HttpAdminRepository,
  HttpBlogRepository,
  HttpGuestbookRepository,
} from './core/infrastructure/http';
import { GlobalErrorHandler } from './core/handlers/global-error.handler';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withViewTransitions(),
      withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' }),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
    ),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        authInterceptor,
        languageInterceptor,
        cacheInterceptor,
        correlationInterceptor,
      ]),
    ),
    provideClientHydration(
      // Explicit transfer-cache config (R6). Empty `{}` relies on Angular defaults
      // which are version-fragile; the predicate scopes the cache to GETs only,
      // strips all headers, and excludes auth + POST so SSR-cached payloads stay
      // safe to hydrate on the client without leaking session-scoped data.
      withHttpTransferCacheOptions({
        filter: (req) => req.method === 'GET',
        includeHeaders: [],
        includePostRequests: false,
        includeRequestsWithAuthHeaders: false,
      }),
    ),
    provideAnimationsAsync(),
    { provide: PORTFOLIO_REPOSITORY, useExisting: HttpPortfolioRepository },
    { provide: ADMIN_REPOSITORY, useExisting: HttpAdminRepository },
    { provide: BLOG_REPOSITORY, useExisting: HttpBlogRepository },
    { provide: GUESTBOOK_REPOSITORY, useExisting: HttpGuestbookRepository },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
